import { signal } from '@angular/core';
import type { UIMessage } from 'ai';
import { v4 } from 'uuid';
import { AI_CHAT_CONTEXT_GUARD_METADATA_KEY, isAIChatContextGuardStatus } from '@jms/model';
import type { AIChatContextGuardStatus } from '@jms/model';
import { AgentUtility } from '../agent.utility';
import type { AnyPart } from '../agent.types';
import type { ToolPartPatch } from './agent.message.state.types';

/**
 * The UI message state a chat adapter feeds the view: the message array plus the part-level
 * mutations a stream applies to it (text and reasoning deltas, tool part transitions, approval
 * markers, placeholders). Owned by the durable adapter; the request-bound adapter keeps its own
 * equivalent so its behaviour stays byte-for-byte unchanged behind the flag.
 */

/** Marker under `callProviderMetadata.workbuddy` that a card went unanswered until it expired. */
export const CARD_EXPIRED_METADATA_FLAG = 'cardExpired';

/**
 * The durable adapter's transcript. Every change goes through these methods so the signal always holds
 * fresh message objects (the view tracks by id, so in-place edits would not render); callers never
 * mutate a returned message.
 */
export class AgentMessageState {

  private readonly $messages = signal<UIMessage[]>([]);

  /** The transcript the view renders, oldest first. */
  public get messages(): UIMessage[] {

    return this.$messages();
  }

  /** Replaces the transcript with copies of `messages` (hydrate, thread switch). */
  public set messages(messages: UIMessage[]) {

    this.$messages.set(messages.map(message => AgentMessageState.cloneMessage(message)));
  }

  /** The newest message, user or assistant. */
  public get lastMessage(): UIMessage | undefined {

    const messages = this.$messages();
    return messages[messages.length - 1];
  }

  /** Tool call ids whose part still waits for a decision. */
  public pendingApprovalToolCallIds(): string[] {

    const ids: string[] = [];
    for (const message of this.$messages()) {
      if (message.role !== 'assistant') continue;
      for (const part of message.parts ?? []) {
        if (AgentUtility.partField<string>(part as AnyPart, 'state', '') !== 'approval-requested') continue;
        const toolCallId = AgentUtility.partField(part as AnyPart, 'toolCallId', '');
        if (toolCallId) ids.push(toolCallId);
      }
    }
    return ids;
  }

  /** The newest context measurement stamped on an assistant message, or null when none was. */
  public readContextGuardStatus(): AIChatContextGuardStatus | null {

    const messages = this.$messages();
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message.role !== 'assistant') continue;
      const metadata = message.metadata as Record<string, unknown> | undefined;
      const status = metadata?.[AI_CHAT_CONTEXT_GUARD_METADATA_KEY];
      if (isAIChatContextGuardStatus(status)) return status;
    }
    return null;
  }

  /** Stamps the live context measurement onto the newest assistant message so surface switches keep it. */
  public applyContextGuardStatus(status: AIChatContextGuardStatus): void {

    this.decorateLastAssistant(AI_CHAT_CONTEXT_GUARD_METADATA_KEY, status);
  }

  /** Sets one metadata key on the newest assistant message; false when the transcript holds none. */
  public decorateLastAssistant(metadataKey: string, value: unknown): boolean {

    let decorated = false;
    this.$messages.update(messages => {
      const next = [...messages];
      for (let index = next.length - 1; index >= 0; index -= 1) {
        const message = next[index];
        if (message.role !== 'assistant') continue;
        next[index] = { ...message, metadata: { ...(message.metadata as Record<string, unknown> | undefined), [metadataKey]: value } } as UIMessage;
        decorated = true;
        return next;
      }
      return messages;
    });
    return decorated;
  }

  /** Appends a copy of a user message the browser painted for its own send. */
  public appendUserMessage(message: UIMessage): void {

    this.$messages.update(messages => [...messages, AgentMessageState.cloneMessage(message)]);
  }

  /** Appends an empty assistant message the next stream chunks fill, and returns it so its id can be renamed later. */
  public appendAssistantPlaceholder(): UIMessage {

    const placeholder = AgentMessageState.createAssistantPlaceholder();
    this.$messages.update(messages => [...messages, placeholder]);
    return placeholder;
  }

  /**
   * A steering message echoed by the loop: it lands before the assistant's next text. When the last
   * message is a still-empty placeholder the user message slips in front of it; otherwise the
   * current reply is closed and a fresh placeholder follows.
   */
  public insertEchoedUserMessage(message: UIMessage): void {

    this.$messages.update(messages => {
      if (messages.some(existing => existing.id === message.id)) return messages;
      const next = [...messages];
      const last = next[next.length - 1];
      if (last?.role === 'assistant' && (last.parts?.length ?? 0) === 0) {
        next.splice(next.length - 1, 0, AgentMessageState.cloneMessage(message));
        return next;
      }
      next.push(AgentMessageState.cloneMessage(message), AgentMessageState.createAssistantPlaceholder());
      return next;
    });
  }

  /** Removes messages by id (a withdrawn steer, a refused send); unknown ids are ignored. */
  public removeMessages(ids: Iterable<string>): void {

    const removed = new Set(ids);
    if (removed.size === 0) return;
    this.$messages.update(messages => messages.filter(message => !removed.has(message.id)));
  }

  /**
   * Takes the transcript from storage without losing what storage has not caught up with.
   *
   * A run watched from a subscription streams into a local reply bubble; once the run ends, storage
   * owns that reply. `supersededIds` names the bubbles the ended run wrote into, so the stored copy
   * REPLACES them instead of standing a second copy beside them. Anything else the browser holds and
   * storage does not (an optimistic message still in flight) is kept, in its current order.
   */
  public reconcileFromStorage(stored: UIMessage[], supersededIds: Iterable<string> = []): void {

    const superseded = new Set(supersededIds);
    // A stored assistant message may stand for several per-step messages joined at read time; a
    // live bubble that adopted one of those step ids is replaced by the joined copy, not kept beside it.
    const storedIds = new Set(stored.flatMap(message => [message.id, ...AgentUtility.mergedMessageIds(message)]));
    this.$messages.update(messages => {
      // The messages route returns ONE page — the newest fifty — not the whole thread. On a longer
      // chat everything the browser holds from before that page is real history, so the page is
      // SPLICED IN at the first message it and the browser share instead of being treated as the
      // transcript; keeping those older messages "because storage did not mention them" would
      // re-append the start of the conversation after the newest reply. Nothing is dropped: what
      // sits past the splice point and storage has not caught up with is still kept, in its order.
      const split = AgentMessageState.pageSplitIndex(messages, storedIds, superseded);
      const older = messages.slice(0, split).filter(message => !superseded.has(message.id));
      const kept = messages.slice(split).filter(message => !AgentMessageState.sharesStoredId(message, storedIds) && !superseded.has(message.id) && (message.parts?.length ?? 0) > 0);
      return [...older, ...stored, ...kept].map(message => AgentMessageState.cloneMessage(message));
    });
  }

  /**
   * Whether a browser message is on the stored page, by its own id or one of the step ids it joined.
   * A joined bubble whose later steps open the page is the same reply: matching only its first id
   * kept it beside the stored copy and showed part of the answer twice. It is replaced by the page's
   * copy, which is also what a fresh load of the page shows.
   */
  private static sharesStoredId(message: UIMessage, storedIds: Set<string>): boolean {

    return storedIds.has(message.id) || AgentUtility.mergedMessageIds(message).some(id => storedIds.has(id));
  }

  /**
   * Where a stored page joins the browser's transcript: messages before this index are older history
   * the page no longer covers, messages from it on are the page's or newer.
   *
   * The first message both sides hold is the join. A long run can push every shared id out of the
   * newest-fifty page, and then the first bubble the ended run wrote into marks it instead, since the
   * page holds that reply and everything before it is history. With neither, the page is newer than
   * everything on screen; treating the browser's messages as newer instead is what moved the start of
   * the conversation below the latest reply.
   */
  private static pageSplitIndex(messages: UIMessage[], storedIds: Set<string>, superseded: Set<string>): number {

    const anchor = messages.findIndex(message => AgentMessageState.sharesStoredId(message, storedIds));
    if (anchor !== -1) return anchor;
    const firstSuperseded = messages.findIndex(message => superseded.has(message.id));
    if (firstSuperseded !== -1) return firstSuperseded;
    return storedIds.size > 0 ? messages.length : 0;
  }

  /**
   * Re-keys a message onto the id storage will hold for it.
   *
   * That id can already be on the transcript — a stored copy of the same reply is standing where
   * the rename lands. Two messages sharing one id render as two identical bubbles, silently: the
   * list tracks by id and Angular only warns about duplicate track keys in a development build. So
   * the pair collapses to one, and whichever copy carries content is the one that survives.
   */
  public renameMessage(currentId: string, nextId: string): void {

    if (currentId === nextId) return;
    this.$messages.update(messages => {
      const renamed = messages.find(message => message.id === currentId);
      if (!renamed) return messages;
      const taken = messages.find(message => message.id === nextId);
      if (!taken) return messages.map(message => message.id === currentId ? { ...message, id: nextId } : message);
      const dropId = (renamed.parts?.length ?? 0) > (taken.parts?.length ?? 0) ? nextId : currentId;
      return messages
        .filter(message => message.id !== dropId)
        .map(message => message.id === currentId ? { ...message, id: nextId } : message);
    });
  }

  /** Drops the newest message when it is an assistant placeholder that never received a part. */
  public removeEmptyTrailingAssistant(): void {

    this.$messages.update(messages => {
      const last = messages[messages.length - 1];
      if (!last || last.role !== 'assistant' || (last.parts?.length ?? 0) > 0) return messages;
      return messages.slice(0, -1);
    });
  }

  /** Appends streamed text to the newest assistant message, extending its last text part or starting one. */
  public appendTextDelta(delta: string): void {

    if (!delta) return;
    this.withLastAssistantMessage(message => {
      const lastPart = message.parts[message.parts.length - 1];
      if (lastPart?.type === 'text') {
        const current = AgentUtility.partField<string>(lastPart, 'text', '');
        message.parts[message.parts.length - 1] = { ...lastPart, text: current + delta } as AnyPart;
      } else {
        message.parts.push({ type: 'text', text: delta } as AnyPart);
      }
    });
  }

  /** Opens a text part on the newest assistant message unless its last part already is one. */
  public startTextPart(): void {

    this.withLastAssistantMessage(message => {
      const lastPart = message.parts[message.parts.length - 1];
      if (lastPart?.type === 'text') return;
      message.parts.push({ type: 'text', text: '' } as AnyPart);
    });
  }

  /** Opens a fresh reasoning part on the newest assistant message. */
  public startReasoningPart(): void {

    this.withLastAssistantMessage(message => {
      message.parts.push({ type: 'reasoning', text: '' } as AnyPart);
    });
  }

  /** Appends streamed reasoning to the newest assistant message, extending its last reasoning part or starting one. */
  public appendReasoningDelta(delta: string): void {

    if (!delta) return;
    this.withLastAssistantMessage(message => {
      const lastPart = message.parts[message.parts.length - 1];
      if (lastPart?.type === 'reasoning') {
        const current = AgentUtility.partField<string>(lastPart, 'text', '');
        message.parts[message.parts.length - 1] = { ...lastPart, text: current + delta } as AnyPart;
      } else {
        message.parts.push({ type: 'reasoning', text: delta } as AnyPart);
      }
    });
  }

  /** Upserts the tool part with this call id on the newest assistant message. */
  public updateToolPart(toolCallId: string, patch: ToolPartPatch): void {

    this.withLastAssistantMessage(message => {
      const partIndex = message.parts.findIndex(part => AgentUtility.partField(part, 'toolCallId', '') === toolCallId);
      const existing = (partIndex >= 0 ? message.parts[partIndex] : { type: 'dynamic-tool' }) as AnyPart;
      const nextPart = {
        ...existing,
        type: partIndex >= 0 ? existing.type : 'dynamic-tool',
        toolCallId,
        toolName: patch.toolName ?? AgentUtility.partField(existing, 'toolName', ''),
        state: patch.state ?? AgentUtility.partField(existing, 'state', ''),
        input: patch.input ?? AgentUtility.partField(existing, 'input', undefined),
        output: patch.output ?? AgentUtility.partField(existing, 'output', undefined),
        errorText: patch.errorText ?? AgentUtility.partField(existing, 'errorText', ''),
        approval: patch.approval ?? AgentUtility.partField(existing, 'approval', undefined),
        callProviderMetadata: patch.callProviderMetadata ?? AgentUtility.partField(existing, 'callProviderMetadata', undefined),
      } as AnyPart;
      if (partIndex >= 0) {
        message.parts[partIndex] = nextPart;
      } else {
        message.parts.push(nextPart);
      }
    });
  }

  /** Patches every tool part with this call id, wherever it sits in the thread. */
  public patchToolPartEverywhere(toolCallId: string, patch: ToolPartPatch): void {

    this.$messages.update(messages => messages.map(message => {
      if (message.role !== 'assistant') return message;
      let changed = false;
      const parts = message.parts.map(part => {
        if (AgentUtility.partField(part as AnyPart, 'toolCallId', '') !== toolCallId) return part;
        changed = true;
        return { ...part, ...patch } as AnyPart;
      });
      return changed ? { ...message, parts } : message;
    }));
  }

  /**
   * Cards that will never be answered (expired, stopped while waiting, or the run ended) settle
   * into the calm denied state; expired ones are flagged so the step reads "expired", not "declined".
   */
  public settlePendingApprovals(expired: boolean): void {

    for (const toolCallId of this.pendingApprovalToolCallIds()) this.settleCard(toolCallId, expired);
  }

  /**
   * Settles one card into the calm denied state, whatever state it is in now. The durable adapter
   * calls it for a card it holds as answered, which `settlePendingApprovals` does not visit.
   */
  public settleCard(toolCallId: string, expired: boolean): void {

    const metadata = expired ? { workbuddy: { [CARD_EXPIRED_METADATA_FLAG]: true } } : undefined;
    this.patchToolPartEverywhere(toolCallId, { state: 'output-denied', errorText: '', ...(metadata ? { callProviderMetadata: metadata } : {}) });
  }

  /** Non-terminal tool parts on the newest assistant message settle calmly once the run has ended. */
  public settleUnfinishedToolParts(): void {

    this.withLastAssistantMessage(message => {
      message.parts = message.parts.map(part => {
        if (!AgentUtility.isToolPartWithCallId(part)) return part;
        const state = AgentUtility.partField<string>(part, 'state', '');
        if (AgentUtility.isTerminalToolState(state)) return part;
        return { ...part, state: 'output-denied', errorText: '' } as AnyPart;
      });
    });
  }

  /** Whether any assistant message in the transcript holds a tool part with this call id. */
  public hasToolPart(toolCallId: string): boolean {

    return this.$messages().some(message => message.role === 'assistant' && (message.parts ?? []).some(part => AgentUtility.partField(part as AnyPart, 'toolCallId', '') === toolCallId));
  }

  /** Whether the newest part for this tool call already records an outcome (output, error or denial). */
  public hasOutputForToolCall(toolCallId: string): boolean {

    for (const message of [...this.$messages()].reverse()) {
      if (message.role !== 'assistant') continue;
      const toolPart = message.parts?.find(part => AgentUtility.partField(part, 'toolCallId', '') === toolCallId);
      if (!toolPart) continue;
      const state = AgentUtility.partField<string>(toolPart, 'state', '');
      return state === 'output-available' || state === 'output-error' || state === 'output-denied';
    }
    return false;
  }

  private withLastAssistantMessage(mutator: (message: UIMessage) => void): void {

    this.$messages.update(messages => {
      const next = messages.map(message => AgentMessageState.cloneMessage(message));
      let assistantIndex = -1;
      for (let index = next.length - 1; index >= 0; index -= 1) {
        if (next[index].role === 'assistant') {
          assistantIndex = index;
          break;
        }
      }
      if (assistantIndex === -1) {
        next.push(AgentMessageState.createAssistantPlaceholder());
        assistantIndex = next.length - 1;
      }
      mutator(next[assistantIndex]);
      return next;
    });
  }

  /** An empty assistant message with a fresh id. */
  public static createAssistantPlaceholder(): UIMessage {

    return { id: v4(), role: 'assistant', parts: [], createdAt: new Date() } as UIMessage;
  }

  /** A copy deep enough that updating its parts never touches the original. */
  public static cloneMessage(message: UIMessage): UIMessage {

    return { ...message, parts: Array.isArray(message.parts) ? message.parts.map(part => ({ ...part } as AnyPart)) : [] };
  }
}
